import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import {
  ICareerResponse,
  ICareerUpdateRequest,
  ICreateCareerRequest,
  ILeagueBySportNameResponse,
  ISportExperienceResponse,
  ITeamsByLeagueNameResponse,
} from '../../interfaces/career.interface';
import { baseQueryWithInterceptor } from '../utils/baseQueryWithInterceptor';

const careerApi = createApi({
  reducerPath: 'careerApi',
  baseQuery: baseQueryWithInterceptor(`${config.API_BASE_URL}/career`),
  tagTypes: ['career'],

  endpoints: (builder) => ({
    getLeagueBySportName: builder.query<ILeagueBySportNameResponse[], string>({
      query: (name) => `/leagues/sportName/${name}`,
      providesTags: ['career'],
    }),
    getTeamsByLeagueName: builder.query<ITeamsByLeagueNameResponse[], string>({
      query: (name) => `/teams/leagueName/${name}`,
      providesTags: ['career'],
    }),
    getLeaguesByMultipleSports: builder.query<
      ILeagueBySportNameResponse[],
      string
    >({
      query: (sports) => `/leagues/by-multiple-sports?sports=${sports}`,
      providesTags: ['career'],
    }),
    getTeamsByMultipleLeagues: builder.query<
      ITeamsByLeagueNameResponse[],
      string
    >({
      query: (leagues) => `/teams/by-multiple-leagues?leagues=${leagues}`,
      providesTags: ['career'],
    }),
    getTeamsByLeagueIdOrExternalId: builder.query<
      ITeamsByLeagueNameResponse[],
      string
    >({
      query: (leagueIdOrExternalId) =>
        `/teams/leagueIdOrExternalId/${leagueIdOrExternalId}`,
      providesTags: ['career'],
    }),
    getUserSportExperienceById: builder.query<
      ISportExperienceResponse[],
      string
    >({
      query: (userId) => `/calculate-sport-experience/${userId}`,
      providesTags: ['career'],
    }),
    createCareer: builder.mutation<ICareerResponse, ICreateCareerRequest>({
      query: (data) => ({
        url: ``,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['career'],
    }),
    updateCareer: builder.mutation<
      ICareerResponse,
      { id: string; data: ICareerUpdateRequest }
    >({
      query: ({ id, data }) => ({
        url: '/' + id,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['career'],
    }),
    deleteCareer: builder.mutation<ICareerResponse, string>({
      query: (id) => ({
        url: '/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['career'],
    }),
  }),
});

export const {
  useGetLeagueBySportNameQuery,
  useGetTeamsByLeagueNameQuery,
  useGetLeaguesByMultipleSportsQuery,
  useGetTeamsByMultipleLeaguesQuery,
  useGetTeamsByLeagueIdOrExternalIdQuery,
  useCreateCareerMutation,
  useUpdateCareerMutation,
  useDeleteCareerMutation,
  useGetUserSportExperienceByIdQuery,
} = careerApi;

export default careerApi;
